import React, { useState, useCallback } from 'react'
import { Box, IconButton, Divider, Chip, Typography } from '@mui/material'
import { Editor } from '@tiptap/react'
import { LinkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { EMAIL_VARIABLES, type EmailVariableId } from './VariableChipExtension'
import { getEquipmentText } from './emailEditorDefaults'
import { LinkPopover } from './LinkPopover'

const TEXT_COLOR_PRESETS = [
  { value: '', label: 'Default', color: INTERAX_COLORS.typographyText },
  { value: INTERAX_COLORS.sun, label: 'Accent', color: INTERAX_COLORS.sun },
  { value: INTERAX_COLORS.offBlack, label: 'Dark', color: INTERAX_COLORS.offBlack }
]

const BACKGROUND_COLOR_PRESETS = [
  { value: '', label: 'None', color: 'transparent' },
  { value: INTERAX_COLORS.sun20, label: 'Light', color: INTERAX_COLORS.sun20 },
  { value: INTERAX_COLORS.sun40, label: 'Medium', color: INTERAX_COLORS.sun40 },
  { value: INTERAX_COLORS.thunderGrey20, label: 'Grey', color: INTERAX_COLORS.thunderGrey20 }
]

interface EmailEditorToolbarProps {
  editor: Editor | null
}

export const EmailEditorToolbar: React.FC<EmailEditorToolbarProps> = ({ editor }) => {
  const { t } = useTranslation()
  const [linkAnchorEl, setLinkAnchorEl] = useState<HTMLElement | null>(null)

  const handleLinkClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setLinkAnchorEl(e.currentTarget)
  }, [])

  const handleInsertVariable = useCallback(
    (variableId: EmailVariableId) => {
      editor?.chain().focus().insertVariable(variableId).run()
    },
    [editor]
  )

  const handleInsertActivitiesBlock = useCallback(() => {
    editor?.chain().focus().insertActivitiesBlock().run()
  }, [editor])

  const handleInsertEquipment = useCallback(() => {
    if (!editor) return
    const html = getEquipmentText(t)
    editor.chain().focus().insertContent(html).run()
  }, [editor, t])

  if (!editor) return null

  const ToolbarButton = ({
    onClick,
    isActive,
    children,
    title
  }: {
    onClick: () => void
    isActive?: boolean
    children: React.ReactNode
    title: string
  }) => (
    <IconButton
      onClick={onClick}
      title={title}
      size="small"
      aria-label={title}
      sx={{
        color: isActive ? INTERAX_COLORS.sun : INTERAX_COLORS.typographyText,
        bgcolor: isActive ? INTERAX_COLORS.heritageBlue : 'transparent',
        borderRadius: 1,
        width: 32,
        height: 32,
        fontSize: '14px',
        fontWeight: isActive ? 700 : 400,
        '&:hover': {
          bgcolor: INTERAX_COLORS.heritageBlue
        }
      }}
    >
      {children}
    </IconButton>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        borderBottom: 1,
        borderColor: INTERAX_COLORS.heritageBlue
      }}
    >
      {/* Row 1: Formatting */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          p: 0.5,
          flexWrap: 'wrap'
        }}
      >
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title={t('facilitator.trainingSprint.emailEditor.bold')}
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title={t('facilitator.trainingSprint.emailEditor.italic')}
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title={t('facilitator.trainingSprint.emailEditor.underline')}
        >
          <u>U</u>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title={t('facilitator.trainingSprint.emailEditor.strikethrough')}
        >
          <s>S</s>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title={t('facilitator.trainingSprint.emailEditor.bulletList')}
        >
          &#8226;
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title={t('facilitator.trainingSprint.emailEditor.orderedList')}
        >
          1.
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: INTERAX_COLORS.heritageBlue }} />

        {/* Link */}
        <IconButton
          onClick={handleLinkClick}
          title={t('facilitator.trainingSprint.emailEditor.insertLink')}
          size="small"
          aria-label={t('facilitator.trainingSprint.emailEditor.insertLink')}
          sx={{
            color: editor.isActive('link') ? INTERAX_COLORS.sun : INTERAX_COLORS.typographyText,
            bgcolor: editor.isActive('link') ? INTERAX_COLORS.heritageBlue : 'transparent',
            borderRadius: 1,
            width: 32,
            height: 32,
            '&:hover': { bgcolor: INTERAX_COLORS.heritageBlue }
          }}
        >
          <LinkIcon style={{ width: 18, height: 18 }} />
        </IconButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: INTERAX_COLORS.heritageBlue }} />

        {/* Text color */}
        {TEXT_COLOR_PRESETS.map((preset) => (
          <IconButton
            key={preset.value || 'default'}
            size="small"
            title={preset.label}
            onClick={() => {
              if (preset.value) {
                editor.chain().focus().setColor(preset.value).run()
              } else {
                editor.chain().focus().unsetColor().run()
              }
            }}
            sx={{
              width: 24,
              height: 24,
              p: 0,
              border: `2px solid ${(editor.getAttributes('textStyle') as { color?: string })?.color === preset.value ? INTERAX_COLORS.sun : 'transparent'}`,
              borderRadius: 0.5,
              '&:hover': { bgcolor: INTERAX_COLORS.heritageBlue }
            }}
          >
            <Box sx={{ width: 14, height: 14, bgcolor: preset.color, borderRadius: 0.25 }} />
          </IconButton>
        ))}

        {/* Background color for text */}
        {BACKGROUND_COLOR_PRESETS.map((preset) => (
          <IconButton
            key={preset.value || 'none'}
            size="small"
            title={preset.label}
            onClick={() => {
              if (preset.value) {
                editor.chain().focus().setBackgroundColor(preset.value).run()
              } else {
                editor.chain().focus().unsetBackgroundColor().run()
              }
            }}
            sx={{
              width: 24,
              height: 24,
              p: 0,
              borderRadius: 0.5,
              '&:hover': { bgcolor: INTERAX_COLORS.heritageBlue }
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                bgcolor: preset.color ?? 'transparent',
                border: '1px solid ' + INTERAX_COLORS.thunderGrey,
                borderRadius: 0.25
              }}
            />
          </IconButton>
        ))}
      </Box>

      {/* Row 2: Insert variable + Equipment */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 0.5,
          py: 0.75,
          borderTop: 1,
          borderColor: INTERAX_COLORS.heritageBlue40,
          flexWrap: 'wrap'
        }}
      >
        <Typography component="span" variant="body2" sx={{ color: INTERAX_COLORS.thunderGrey, fontWeight: 500 }}>
          {t('facilitator.trainingSprint.emailEditor.insertVariable')}:
        </Typography>
        {EMAIL_VARIABLES.map((variable) => (
          <Chip
            key={variable.id}
            label={`{{${variable.id}}}`}
            size="small"
            onClick={() => handleInsertVariable(variable.id)}
            aria-label={`${t('facilitator.trainingSprint.emailEditor.insertVariable')}: ${variable.label}`}
            sx={{
              bgcolor: '#3b82f6',
              color: '#fff',
              fontSize: '11px',
              height: 24,
              cursor: 'pointer',
              '&:hover': { bgcolor: '#2563eb' }
            }}
          />
        ))}
        <Chip
          label={t('facilitator.trainingSprint.emailEditor.activitiesSchedule')}
          size="small"
          onClick={handleInsertActivitiesBlock}
          sx={{
            bgcolor: INTERAX_COLORS.heritageBlue40,
            color: INTERAX_COLORS.typographyText,
            fontSize: '11px',
            height: 24,
            cursor: 'pointer',
            border: `1px solid ${INTERAX_COLORS.thunderGrey}`,
            '&:hover': { bgcolor: INTERAX_COLORS.heritageBlue }
          }}
        />
        <Chip
          label={t('facilitator.trainingSprint.emailEditor.sectionEquipment')}
          size="small"
          onClick={handleInsertEquipment}
          sx={{
            bgcolor: INTERAX_COLORS.heritageBlue,
            color: INTERAX_COLORS.typographyText,
            fontSize: '11px',
            height: 24,
            cursor: 'pointer',
            border: `1px solid ${INTERAX_COLORS.thunderGrey}`,
            '&:hover': { bgcolor: INTERAX_COLORS.heritageBlue40 }
          }}
        />
      </Box>

      <LinkPopover editor={editor} anchorEl={linkAnchorEl} onClose={() => setLinkAnchorEl(null)} />
    </Box>
  )
}
